import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { API_URL } from "../services/api";
import {
  AlertCircleIcon,
  UploadCloudIcon,
  RefreshCwIcon,
  CheckBadgeIcon
} from "../components/Icons";

function VerificationCenter({ user }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "overview";

  const [activeTab, setActiveTab] = useState(initialTab); // 'overview' | 'documents' | 'bank' | 'audit'
  const [readiness, setReadiness] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [identityDetails, setIdentityDetails] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Document Upload Modal state
  const [showDocModal, setShowDocModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("aadhaar");
  const [docNumber, setDocNumber] = useState("");
  const [docFile, setDocFile] = useState("");
  const [frontImage, setFrontImage] = useState("");
  const [backImage, setBackImage] = useState("");
  const [modalUploadState, setModalUploadState] = useState({
    front: { stage: "idle", percent: 0, filename: "" },
    back: { stage: "idle", percent: 0, filename: "" },
  });

  // Bank Edit state
  const [showBankForm, setShowBankForm] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [showAcc, setShowAcc] = useState(false);
  const [showConfirmAcc, setShowConfirmAcc] = useState(false);
  const [ifsc, setIfsc] = useState("");
  const [accountType, setAccountType] = useState("current");
  const [upiId, setUpiId] = useState("");

  // IFSC Lookup State
  const [ifscLoading, setIfscLoading] = useState(false);
  const [ifscError, setIfscError] = useState("");
  const [ifscSuccess, setIfscSuccess] = useState(null);

  const getFullFileUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    if (path.startsWith("public")) return `${API_URL}/${path}`;
    return `${API_URL}/${path.replace(/^\//, "")}`;
  };

  const handleIfscChange = async (val) => {
    const clean = val.toUpperCase().trim();
    setIfsc(clean);
    setIfscError("");
    setIfscSuccess(null);

    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (clean.length === 11) {
      if (!ifscRegex.test(clean)) {
        setIfscError("Invalid IFSC format. Must be 4 letters, 0, and 6 alphanumeric characters.");
        return;
      }

      try {
        setIfscLoading(true);
        const res = await api.get(`/vendor/bank/ifsc/${clean}`);
        if (res.data && res.data.success) {
          setBankName(res.data.bankName || "");
          setIfscSuccess({
            bankName: res.data.bankName,
            branch: res.data.branch,
            city: res.data.city,
            state: res.data.state,
          });
        }
      } catch (err) {
        setBankName("");
        setIfscError(
          err.response?.data?.msg || "Invalid IFSC code. Bank branch not found."
        );
      } finally {
        setIfscLoading(false);
      }
    } else {
      setBankName("");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const [readinessRes, docsRes, bankRes] = await Promise.all([
        api.get("/vendor/verification/status"),
        api.get("/vendor/documents"),
        api.get("/vendor/bank")
      ]);

      setReadiness(readinessRes.data);
      setDocuments(docsRes.data.documents || []);
      setIdentityDetails(docsRes.data.identityDetails || null);
      setAuditLog(docsRes.data.auditLog || []);
      setBank(bankRes.data.bank || null);

      if (bankRes.data.bank) {
        setAccountHolderName(bankRes.data.bank.accountHolderName || "");
        setBankName(bankRes.data.bank.bankName || "");
        setIfsc(bankRes.data.bank.ifsc || "");
        setAccountType(bankRes.data.bank.accountType || "current");
        setUpiId(bankRes.data.bank.upiId || "");
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load verification details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFileUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError("File is too large. Maximum supported size is 5MB.");
      return;
    }

    const validMimes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validMimes.includes(file.type)) {
      setError("Please upload a JPEG, PNG, WEBP image or PDF document.");
      return;
    }

    setError("");
    const formattedSize =
      file.size > 1024 * 1024
        ? (file.size / (1024 * 1024)).toFixed(2) + " MB"
        : (file.size / 1024).toFixed(1) + " KB";
    const localPreview = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";

    setModalUploadState((prev) => ({
      ...prev,
      [target]: {
        stage: "uploading",
        percent: 30,
        filename: file.name,
        size: formattedSize,
        previewUrl: localPreview,
      },
    }));
    setUploadingDoc(true);

    const formData = new FormData();
    formData.append("document", file);

    const timer = setInterval(() => {
      setModalUploadState((prev) => {
        const p = prev[target]?.percent || 30;
        if (p < 85) return { ...prev, [target]: { ...prev[target], percent: p + 15 } };
        return prev;
      });
    }, 150);

    try {
      const res = await api.post("/vendor/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clearInterval(timer);

      if (res.data.success && res.data.filePath) {
        setModalUploadState((prev) => ({
          ...prev,
          [target]: { ...prev[target], stage: "processing", percent: 92 },
        }));

        setTimeout(() => {
          setModalUploadState((prev) => ({
            ...prev,
            [target]: { ...prev[target], percent: 98 },
          }));
        }, 400);

        setTimeout(() => {
          if (target === "front") setFrontImage(res.data.filePath);
          else if (target === "back") setBackImage(res.data.filePath);
          else setDocFile(res.data.filePath);

          setModalUploadState((prev) => ({
            ...prev,
            [target]: { ...prev[target], stage: "completed", percent: 100 },
          }));
          setUploadingDoc(false);
        }, 900);
      }
    } catch (err) {
      clearInterval(timer);
      setError(err.response?.data?.msg || "Failed to upload file. Please try again.");
      setModalUploadState((prev) => ({
        ...prev,
        [target]: { stage: "idle", percent: 0, filename: "", size: "", previewUrl: "" },
      }));
      setUploadingDoc(false);
    }
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    if (!docNumber.trim()) {
      setError("Please enter the document / identity number.");
      return;
    }

    try {
      setUploadingDoc(true);
      const docNameMap = {
        aadhaar: "Aadhaar Card",
        pan: "PAN Card",
        gst: "GST Certificate",
        bank_proof: "Bank Proof / Cancelled Cheque"
      };

      const res = await api.post("/vendor/documents", {
        documentType: selectedDocType,
        documentName: docNameMap[selectedDocType] || selectedDocType.toUpperCase(),
        documentNumber: docNumber.trim(),
        documentFile: docFile || frontImage,
        frontImage: frontImage || docFile,
        backImage: backImage
      });

      setSuccess(res.data.message || "Document submitted successfully for review!");
      setDocuments(res.data.documents || []);
      if (res.data.readiness) setReadiness(res.data.readiness);
      if (res.data.identityDetails) setIdentityDetails(res.data.identityDetails);
      if (res.data.auditLog) setAuditLog(res.data.auditLog);

      setShowDocModal(false);
      setDocNumber("");
      setDocFile("");
      setFrontImage("");
      setBackImage("");
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to submit document.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!accountHolderName.trim() || !bankName.trim() || !accountNumber.trim() || !ifsc.trim()) {
      setError("Please fill all required bank fields.");
      return;
    }

    if (accountNumber !== confirmAccountNumber) {
      setError("Account numbers do not match. Please verify both fields.");
      return;
    }

    try {
      setSavingBank(true);
      setError("");
      setSuccess("");

      const res = await api.post("/vendor/bank/verify", {
        accountHolderName: accountHolderName.trim(),
        accountNumber: accountNumber.trim(),
        confirmAccountNumber: confirmAccountNumber.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        accountType,
        upiId: upiId.trim()
      });

      if (res.data.verified) {
        setSuccess(res.data.message || "✓ Bank account verified successfully!");
      } else if (res.data.status === "under_review") {
        setSuccess(res.data.message || "Bank details submitted and queued for review.");
      } else {
        setSuccess(res.data.message || "Bank details updated.");
      }

      setBank(res.data.bank);
      if (res.data.readiness) setReadiness(res.data.readiness);
      setShowBankForm(false);
      setAccountNumber("");
      setConfirmAccountNumber("");
    } catch (err) {
      setError(err.response?.data?.msg || err.response?.data?.error || "Failed to verify bank details.");
    } finally {
      setSavingBank(false);
    }
  };

  const openDocUpload = (docType) => {
    setSelectedDocType(docType);
    setError("");
    setSuccess("");
    const existing = documents.find((d) => d.documentType === docType);
    setDocNumber(existing?.documentNumberMasked || "");
    setFrontImage(existing?.frontImage || existing?.documentFile || "");
    setBackImage(existing?.backImage || "");
    setDocFile(existing?.documentFile || "");
    setShowDocModal(true);
  };

  const renderBadge = (status) => {
    switch (status) {
      case "verified":
        return (
          <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ✓ Verified
          </span>
        );
      case "under_review":
      case "pending":
        return (
          <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ⏳ Under Review
          </span>
        );
      case "rejected":
      case "requires_reupload":
      case "requires_update":
        return (
          <span style={{ background: "#fee2e2", color: "#991b1b", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ⚠ Action Required
          </span>
        );
      default:
        return (
          <span style={{ background: "#fee2e2", color: "#b91c1c", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ● Required
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <div className="spinner-border text-brand-primary" role="status" style={{ width: "38px", height: "38px" }} />
        <p style={{ color: "#64748b", marginTop: "14px", fontWeight: "700" }}>Loading Verification Center...</p>
      </div>
    );
  }

  const reqs = readiness?.requirements || {};
  const progressPct = reqs.progressPercentage || 25;
  const isFullyVerified = readiness?.isEligible || false;
  const nextAction = readiness?.nextAction;

  return (
    <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
      {/* ═══════════ HEADER ═══════════ */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="section-badge">SELLER COMPLIANCE</span>
            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "700" }}>• Real-Time Verification Engine</span>
          </div>
          <h2 style={{ fontSize: "24px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
            Verification &amp; Compliance Center
          </h2>
          <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0 }}>
            Manage identity verification documents, bank payouts, and product listing eligibility.
          </p>
        </div>

        <button
          onClick={fetchData}
          className="btn btn-outline-secondary btn-sm"
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: "700" }}
        >
          <RefreshCwIcon size={14} />
          <span>Refresh Status</span>
        </button>
      </div>

      {/* ═══════════ ALERTS ═══════════ */}
      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", fontSize: "13px", color: "#b91c1c", display: "flex", alignItems: "center", gap: "8px" }}>
          <AlertCircleIcon size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ background: "#dcfce7", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", fontSize: "13px", color: "#166534", display: "flex", alignItems: "center", gap: "8px" }}>
          <CheckBadgeIcon size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* ═══════════ SMART NEXT ACTION BANNER ═══════════ */}
      <div
        className="card-brand"
        style={{
          background: isFullyVerified
            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
          border: isFullyVerified ? "1.5px solid #86efac" : "1.5px solid #fde68a",
          borderRadius: "14px",
          padding: "22px 26px",
          marginBottom: "28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1, minWidth: "260px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: isFullyVerified ? "#16a34a" : "#d97706",
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              fontSize: "22px",
              flexShrink: 0
            }}
          >
            {isFullyVerified ? "✓" : "⚡"}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
              <span style={{ fontSize: "11.5px", fontWeight: "900", letterSpacing: "1px", textTransform: "uppercase", color: isFullyVerified ? "#166534" : "#92400e" }}>
                {isFullyVerified ? "VERIFICATION COMPLETED" : `VERIFICATION ${progressPct}% COMPLETE`}
              </span>
            </div>
            <h3 style={{ fontSize: "17px", fontWeight: "900", color: "#0f172a", margin: "2px 0" }}>
              {nextAction?.title || (isFullyVerified ? "Product Listing Unlocked" : "Action Required")}
            </h3>
            <p style={{ fontSize: "13px", color: isFullyVerified ? "#166534" : "#78350f", margin: 0 }}>
              {nextAction?.description || "Complete all verification milestones to receive customer orders."}
            </p>
          </div>
        </div>

        <div>
          {isFullyVerified ? (
            <Link to="/vendor/products/new" className="btn btn-primary" style={{ padding: "10px 22px", fontSize: "13.5px", fontWeight: "800" }}>
              Add New Product &rarr;
            </Link>
          ) : nextAction?.type === "bank" ? (
            <button
              onClick={() => { setActiveTab("bank"); setShowBankForm(true); }}
              className="btn btn-primary"
              style={{ padding: "10px 22px", fontSize: "13.5px", fontWeight: "800" }}
            >
              Complete Bank Verification &rarr;
            </button>
          ) : (
            <button
              onClick={() => setActiveTab("documents")}
              className="btn btn-primary"
              style={{ padding: "10px 22px", fontSize: "13.5px", fontWeight: "800" }}
            >
              Upload Required Documents &rarr;
            </button>
          )}
        </div>
      </div>

      {/* ═══════════ 5-POINT STATUS TIMELINE ═══════════ */}
      <div className="card-brand" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px 24px", marginBottom: "28px" }}>
        <h4 style={{ fontSize: "14px", fontWeight: "900", color: "#0f172a", marginBottom: "16px" }}>
          Onboarding &amp; Verification Milestone Timeline
        </h4>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px" }}>
          {/* Milestone 1: Application */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>01. APPLICATION</span>
              <span style={{ color: "#16a34a", fontWeight: "900", fontSize: "13px" }}>✓</span>
            </div>
            <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>Seller Application</strong>
            <span style={{ fontSize: "11.5px", color: "#166534", fontWeight: "700" }}>Approved by Admin</span>
          </div>

          {/* Milestone 2: Identity */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>02. IDENTITY</span>
              {reqs.aadhaarVerified && reqs.panVerified ? (
                <span style={{ color: "#16a34a", fontWeight: "900", fontSize: "13px" }}>✓</span>
              ) : (
                <span style={{ color: "#f59e0b", fontWeight: "900", fontSize: "13px" }}>●</span>
              )}
            </div>
            <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>Aadhaar &amp; PAN</strong>
            <span style={{ fontSize: "11.5px", color: reqs.aadhaarVerified && reqs.panVerified ? "#166534" : "#b45309", fontWeight: "700" }}>
              {reqs.aadhaarVerified && reqs.panVerified ? "Verified" : "Under Review"}
            </span>
          </div>

          {/* Milestone 3: Documents */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>03. DOCUMENTS</span>
              {reqs.documentsVerified ? (
                <span style={{ color: "#16a34a", fontWeight: "900", fontSize: "13px" }}>✓</span>
              ) : (
                <span style={{ color: "#f59e0b", fontWeight: "900", fontSize: "13px" }}>●</span>
              )}
            </div>
            <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>Document Uploads</strong>
            <span style={{ fontSize: "11.5px", color: reqs.documentsVerified ? "#166534" : "#b45309", fontWeight: "700" }}>
              {reqs.documentsVerified ? "Verified (2/2)" : `${reqs.documentsSummary?.verifiedCount || 0}/2 Verified`}
            </span>
          </div>

          {/* Milestone 4: Bank */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>04. PAYOUTS</span>
              {reqs.bankVerified ? (
                <span style={{ color: "#16a34a", fontWeight: "900", fontSize: "13px" }}>✓</span>
              ) : (
                <span style={{ color: "#dc2626", fontWeight: "900", fontSize: "13px" }}>●</span>
              )}
            </div>
            <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>Bank Account</strong>
            <span style={{ fontSize: "11.5px", color: reqs.bankVerified ? "#166534" : "#b91c1c", fontWeight: "700" }}>
              {reqs.bankVerified ? "Verified" : reqs.bankStatus === "under_review" ? "Under Review" : "Action Required"}
            </span>
          </div>

          {/* Milestone 5: Listing Lock */}
          <div style={{ background: isFullyVerified ? "#f0fdf4" : "#fff1f2", border: isFullyVerified ? "1px solid #bbf7d0" : "1px solid #fecdd3", borderRadius: "10px", padding: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: isFullyVerified ? "#166534" : "#9f1239" }}>05. CATALOGUE</span>
              <span style={{ fontSize: "13px" }}>{isFullyVerified ? "🔓" : "🔒"}</span>
            </div>
            <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>Product Listing</strong>
            <span style={{ fontSize: "11.5px", color: isFullyVerified ? "#166534" : "#9f1239", fontWeight: "700" }}>
              {isFullyVerified ? "Unlocked ✓" : "Locked"}
            </span>
          </div>
        </div>
      </div>

      {/* ═══════════ TAB CONTROLS ═══════════ */}
      <div style={{ display: "flex", gap: "8px", borderBottom: "1px solid #e2e8f0", marginBottom: "24px" }}>
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "overview" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 16px",
            fontSize: "14px",
            fontWeight: activeTab === "overview" ? "800" : "600",
            color: activeTab === "overview" ? "var(--primary)" : "#64748b",
            cursor: "pointer"
          }}
        >
          Overview &amp; Gates
        </button>

        <button
          onClick={() => setActiveTab("documents")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "documents" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 16px",
            fontSize: "14px",
            fontWeight: activeTab === "documents" ? "800" : "600",
            color: activeTab === "documents" ? "var(--primary)" : "#64748b",
            cursor: "pointer"
          }}
        >
          Identity Documents ({documents.length})
        </button>

        <button
          onClick={() => setActiveTab("bank")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "bank" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 16px",
            fontSize: "14px",
            fontWeight: activeTab === "bank" ? "800" : "600",
            color: activeTab === "bank" ? "var(--primary)" : "#64748b",
            cursor: "pointer"
          }}
        >
          Bank &amp; Payout Details
        </button>

        <button
          onClick={() => setActiveTab("audit")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "audit" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 16px",
            fontSize: "14px",
            fontWeight: activeTab === "audit" ? "800" : "600",
            color: activeTab === "audit" ? "var(--primary)" : "#64748b",
            cursor: "pointer"
          }}
        >
          Verification Audit Trail
        </button>
      </div>

      {/* ═══════════ TAB 1: OVERVIEW ═══════════ */}
      {activeTab === "overview" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
            {/* Identity Summary Card */}
            <div className="card-brand" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                <strong style={{ fontSize: "14px", color: "#0f172a" }}>Official Identity Details</strong>
                <button onClick={() => setActiveTab("documents")} className="btn btn-outline-secondary btn-sm" style={{ fontSize: "11.5px" }}>
                  Manage Docs
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Aadhaar Number</span>
                  <span style={{ fontWeight: "700", color: "#0f172a" }}>
                    {identityDetails?.aadhaarMasked || "•••• •••• 4821"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>PAN Card Number</span>
                  <span style={{ fontWeight: "700", color: "#0f172a" }}>
                    {identityDetails?.panMasked || "ABCDE••••F"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                  <span style={{ color: "#64748b" }}>Identity Verification</span>
                  {renderBadge(reqs.aadhaarVerified && reqs.panVerified ? "verified" : "under_review")}
                </div>
              </div>
            </div>

            {/* Bank Summary Card */}
            <div className="card-brand" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                <strong style={{ fontSize: "14px", color: "#0f172a" }}>Bank Settlement Account</strong>
                <button onClick={() => { setActiveTab("bank"); setShowBankForm(true); }} className="btn btn-outline-secondary btn-sm" style={{ fontSize: "11.5px" }}>
                  Update Bank
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Bank Name</span>
                  <span style={{ fontWeight: "700", color: "#0f172a" }}>
                    {bank?.bankName || "Not Provided"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Account Number</span>
                  <span style={{ fontWeight: "700", color: "#0f172a" }}>
                    {bank?.accountNumberMasked || "Not Submitted"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                  <span style={{ color: "#64748b" }}>Payout Verification</span>
                  {renderBadge(reqs.bankStatus || "not_submitted")}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ TAB 2: DOCUMENTS ═══════════ */}
      {activeTab === "documents" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
            <div>
              <h4 style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a", margin: 0 }}>
                Submitted Identity &amp; Compliance Documents
              </h4>
              <p style={{ fontSize: "13px", color: "#64748b", margin: "2px 0 0" }}>
                Sensitive identity documents are encrypted and protected by strict role permissions.
              </p>
            </div>

            <button onClick={() => openDocUpload("aadhaar")} className="btn btn-primary btn-sm" style={{ fontWeight: "700" }}>
              + Upload Document
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "18px" }}>
            {documents.map((doc, idx) => (
              <div
                key={idx}
                className="card-brand"
                style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                  <div>
                    <strong style={{ fontSize: "14.5px", color: "#0f172a", display: "block" }}>
                      {doc.documentName}
                    </strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      Number: {doc.documentNumberMasked || "Masked"}
                    </span>
                  </div>
                  {renderBadge(doc.status)}
                </div>

                {/* Document File Preview */}
                {(doc.frontImage || doc.documentFile) && (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <img
                      src={getFullFileUrl(doc.frontImage || doc.documentFile)}
                      alt={doc.documentName}
                      style={{ width: "64px", height: "48px", objectFit: "cover", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        Attached File
                      </span>
                      <span style={{ fontSize: "11px", color: "#64748b" }}>
                        Submitted {doc.submittedAt ? new Date(doc.submittedAt).toLocaleDateString() : "Recently"}
                      </span>
                    </div>
                  </div>
                )}

                {doc.rejectionReason && (
                  <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "8px", padding: "10px", fontSize: "12px", color: "#991b1b", marginBottom: "12px" }}>
                    <strong>Admin Note:</strong> {doc.rejectionReason}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
                  <button
                    onClick={() => openDocUpload(doc.documentType)}
                    className="btn btn-outline-secondary btn-sm"
                    style={{ fontSize: "12px", fontWeight: "700" }}
                  >
                    Re-Upload / Replace
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════ TAB 3: BANK & PAYOUTS ═══════════ */}
      {activeTab === "bank" && (
        <div style={{ maxWidth: "680px" }}>
          <div className="card-brand" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <div>
                <h4 style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a", margin: 0 }}>
                  Bank Payout &amp; Settlement Account
                </h4>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "2px 0 0" }}>
                  Verified bank account for automated marketplace settlement payouts.
                </p>
              </div>
              {renderBadge(bank?.verificationStatus || "not_submitted")}
            </div>

            {!showBankForm && bank?.accountNumberMasked ? (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "20px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "13px" }}>
                  <div>
                    <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Account Holder</span>
                    <strong style={{ color: "#0f172a", fontSize: "14.5px" }}>{bank.nameAtBank || bank.accountHolderName}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Bank Name</span>
                    <strong style={{ color: "#0f172a", fontSize: "14.5px" }}>{bank.bankName}</strong>
                    {bank.branchName && (
                      <span style={{ display: "block", fontSize: "11.5px", color: "#64748b" }}>{bank.branchName} Branch</span>
                    )}
                  </div>
                  <div>
                    <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Account Number (Masked)</span>
                    <strong style={{ color: "#0f172a", fontSize: "14.5px", fontFamily: "monospace" }}>{bank.accountNumberMasked}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>IFSC Code</span>
                    <strong style={{ color: "#0f172a", fontSize: "14.5px", fontFamily: "monospace" }}>{bank.ifsc}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Account Type</span>
                    <strong style={{ color: "#0f172a", textTransform: "capitalize" }}>{bank.accountType || "Current"} Account</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>UPI ID</span>
                    <strong style={{ color: "#0f172a" }}>{bank.upiId || "Not set"}</strong>
                  </div>
                </div>

                {bank?.rejectionReason && (
                  <div style={{ marginTop: "16px", background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: "8px", padding: "12px 14px", fontSize: "13px", color: "#991b1b" }}>
                    <strong>✕ Rejection Reason:</strong> {bank.rejectionReason}
                  </div>
                )}

                <div style={{ marginTop: "18px", paddingTop: "14px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                  <span style={{ fontSize: "12px", color: bank.verificationStatus === "verified" ? "#166534" : bank.verificationStatus === "rejected" ? "#dc2626" : "#b45309", fontWeight: "700" }}>
                    {bank.verificationStatus === "verified"
                      ? `🔒 Verified via Cashfree SecureID${bank.verifiedAt ? ` on ${new Date(bank.verifiedAt).toLocaleDateString("en-IN")}` : ""}`
                      : bank.verificationStatus === "rejected"
                      ? "✕ Verification rejected by admin"
                      : "● Bank account under admin review"}
                  </span>
                  <button onClick={() => setShowBankForm(true)} className="btn btn-outline-secondary btn-sm" style={{ fontWeight: "700" }}>
                    {bank.verificationStatus === "rejected" ? "Update Bank Details" : "Change Bank Account"}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleBankSubmit}>
                {/* 1. Account Holder Name */}
                <div style={{ marginBottom: "14px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>1. Account Holder Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    placeholder="Full name as in bank passbook"
                  />
                  <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "3px" }}>
                    Must match registered business name or personal profile.
                  </div>
                </div>

                {/* 2. IFSC Code */}
                <div style={{ marginBottom: "14px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>2. IFSC Code *</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      className="form-control"
                      required
                      maxLength={11}
                      value={ifsc}
                      onChange={(e) => handleIfscChange(e.target.value)}
                      placeholder="e.g. HDFC0001234"
                      style={{ textTransform: "uppercase", fontFamily: "monospace", letterSpacing: "1px" }}
                    />
                    {ifscLoading && (
                      <span
                        className="spinner-border spinner-border-sm text-primary"
                        style={{ position: "absolute", right: "12px", top: "12px" }}
                      />
                    )}
                  </div>
                  {ifscError && (
                    <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", marginTop: "4px" }}>
                      ✕ {ifscError}
                    </div>
                  )}
                  {ifscSuccess && (
                    <div style={{ fontSize: "12px", color: "#166534", fontWeight: "700", marginTop: "4px", background: "#f0fdf4", padding: "6px 10px", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                      ✓ {ifscSuccess.bankName} &middot; {ifscSuccess.branch} ({ifscSuccess.city})
                    </div>
                  )}
                </div>

                {/* 3. Locked Auto Bank Name */}
                <div style={{ marginBottom: "14px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>Bank Name (Auto-Identified) [Locked]</label>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    readOnly
                    value={bankName || "Automatically identified from IFSC code"}
                    style={{ background: "#f1f5f9", fontWeight: "700", cursor: "not-allowed" }}
                  />
                </div>

                {/* 4. Dual Account Numbers */}
                <div className="row">
                  <div className="col-md-6" style={{ marginBottom: "14px" }}>
                    <label className="form-label" style={{ fontWeight: "700" }}>3. Bank Account Number *</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showAcc ? "text" : "password"}
                        className="form-control"
                        required
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                        placeholder="Account number"
                        style={{ paddingRight: "38px", fontFamily: showAcc ? "monospace" : "inherit" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowAcc(!showAcc)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#64748b",
                          fontSize: "15px",
                          cursor: "pointer",
                          padding: "4px"
                        }}
                        title={showAcc ? "Hide account number" : "Show account number"}
                      >
                        {showAcc ? "👁️" : "👁️‍🗨️"}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: "14px" }}>
                    <label className="form-label" style={{ fontWeight: "700" }}>4. Confirm Account Number *</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showConfirmAcc ? "text" : "password"}
                        className="form-control"
                        required
                        value={confirmAccountNumber}
                        onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ""))}
                        placeholder="Re-enter account number"
                        style={{ paddingRight: "38px", fontFamily: showConfirmAcc ? "monospace" : "inherit" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmAcc(!showConfirmAcc)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#64748b",
                          fontSize: "15px",
                          cursor: "pointer",
                          padding: "4px"
                        }}
                        title={showConfirmAcc ? "Hide account number" : "Show account number"}
                      >
                        {showConfirmAcc ? "👁️" : "👁️‍🗨️"}
                      </button>
                    </div>
                  </div>
                </div>

                {accountNumber && confirmAccountNumber && accountNumber === confirmAccountNumber && (
                  <div style={{ fontSize: "12px", color: "#166534", fontWeight: "700", marginBottom: "12px" }}>
                    ✓ Account numbers match
                  </div>
                )}
                {accountNumber && confirmAccountNumber && accountNumber !== confirmAccountNumber && (
                  <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", marginBottom: "12px" }}>
                    ✕ Account numbers do not match
                  </div>
                )}

                {/* Account Type & UPI */}
                <div className="row">
                  <div className="col-md-6" style={{ marginBottom: "14px" }}>
                    <label className="form-label" style={{ fontWeight: "700" }}>Account Type</label>
                    <select className="form-select" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
                      <option value="current">Current Account (Business)</option>
                      <option value="savings">Savings Account (Personal)</option>
                    </select>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: "14px" }}>
                    <label className="form-label" style={{ fontWeight: "700" }}>UPI ID (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value)}
                      placeholder="merchant@okhdfcbank"
                    />
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
                  {bank?.accountNumberMasked && (
                    <button type="button" onClick={() => setShowBankForm(false)} className="btn btn-outline-secondary" style={{ fontSize: "13px" }}>
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={savingBank || !bankName || (accountNumber !== confirmAccountNumber)}
                    style={{ fontSize: "13px", fontWeight: "700" }}
                  >
                    {savingBank ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Verifying with Cashfree Bank Gateway...
                      </>
                    ) : (
                      "🔒 Verify & Save Bank Account"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ TAB 4: AUDIT TRAIL ═══════════ */}
      {activeTab === "audit" && (
        <div style={{ maxWidth: "780px" }}>
          <div className="card-brand" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "24px" }}>
            <h4 style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a", marginBottom: "18px" }}>
              Verification &amp; Compliance Audit History
            </h4>

            {auditLog && auditLog.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {auditLog.map((log, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderLeft: "3px solid var(--primary)",
                      paddingLeft: "14px",
                      paddingBottom: "8px"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                      <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>{log.action}</strong>
                      <span style={{ fontSize: "11.5px", color: "#94a3b8" }}>
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : "Just now"}
                      </span>
                    </div>
                    <p style={{ fontSize: "12.5px", color: "#475569", margin: "2px 0 0" }}>
                      {log.details || "Action recorded by system."} • Performed by: <strong>{log.performedBy || "Seller"}</strong>
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "30px 20px", color: "#64748b" }}>
                <p style={{ margin: 0, fontSize: "13.5px" }}>No recent audit events logged.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ DOCUMENT UPLOAD MODAL ═══════════ */}
      {showDocModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
            padding: "20px"
          }}
        >
          <div
            className="card-brand"
            style={{
              width: "100%",
              maxWidth: "520px",
              background: "#ffffff",
              borderRadius: "16px",
              padding: "28px",
              position: "relative",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
              <strong style={{ fontSize: "17px", fontWeight: "900", color: "#0f172a" }}>
                Upload {selectedDocType.toUpperCase()} Document
              </strong>
              <button
                type="button"
                onClick={() => setShowDocModal(false)}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleDocumentSubmit}>
              <div style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>Document Number / Reference *</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={docNumber}
                  onChange={(e) => setDocNumber(e.target.value)}
                  placeholder={selectedDocType === "aadhaar" ? "12-digit Aadhaar number" : selectedDocType === "pan" ? "10-character PAN number" : "Document reference"}
                />
              </div>

              {/* Upload Front */}
              <div style={{ marginBottom: "18px" }}>
                <label className="form-label" style={{ fontWeight: "700" }}>
                  {selectedDocType === "aadhaar" ? "Front Side Photo / Scan *" : "Document Photo / Scan *"}
                </label>
                {modalUploadState.front.stage === "uploading" || modalUploadState.front.stage === "processing" ? (
                  <div className="doc-processing-card" style={{ border: "1.5px solid #3b82f6", borderRadius: "12px", padding: "16px 18px", position: "relative", overflow: "hidden" }}>
                    <div className="doc-scan-laser" />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      <span style={{ fontSize: "12.5px", color: "#2563eb", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <span className="spinner-border spinner-border-sm" role="status" style={{ width: "12px", height: "12px", borderWidth: "2px" }} />
                        <span>{modalUploadState.front.stage === "processing" ? "Scanning Document Clarity..." : "Uploading securely..."}</span>
                      </span>
                      <span style={{ fontSize: "11.5px", fontWeight: "900", color: "#1d4ed8", background: "#dbeafe", padding: "2px 8px", borderRadius: "6px" }}>
                        {modalUploadState.front.percent}%
                      </span>
                    </div>
                    <div style={{ width: "100%", height: "7px", background: "#e2e8f0", borderRadius: "6px", overflow: "hidden", marginBottom: "12px" }}>
                      <div style={{ width: `${modalUploadState.front.percent}%`, height: "100%", background: "linear-gradient(90deg, #3b82f6, #10b981)", transition: "width 0.25s ease" }} />
                    </div>
                    {modalUploadState.front.filename && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#ffffff", padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                        {modalUploadState.front.previewUrl ? (
                          <div className="doc-preview-wrapper" style={{ position: "relative", width: "48px", height: "34px", flexShrink: 0 }}>
                            <img src={modalUploadState.front.previewUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }} />
                            <div className="doc-scan-grid" />
                          </div>
                        ) : (
                          <span style={{ fontSize: "16px" }}>📄</span>
                        )}
                        <div style={{ minWidth: 0, flex: 1, fontSize: "12px", fontWeight: "700", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {modalUploadState.front.filename}
                        </div>
                      </div>
                    )}
                  </div>
                ) : frontImage ? (
                  <div className="doc-added-card" style={{ border: "1.5px solid #86efac", borderRadius: "12px", padding: "14px 16px", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: 1 }}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <img
                          src={modalUploadState.front.previewUrl || getFullFileUrl(frontImage)}
                          alt="Front"
                          style={{ width: "56px", height: "40px", objectFit: "cover", borderRadius: "6px", border: "1.5px solid #86efac" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <span style={{ position: "absolute", bottom: "-3px", right: "-3px", background: "#16a34a", color: "#ffffff", width: "16px", height: "16px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "900" }}>✓</span>
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <strong style={{ fontSize: "13px", color: "#15803d" }}>Document Added ✓</strong>
                          <span style={{ fontSize: "10px", background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: "8px", fontWeight: "800" }}>Ready</span>
                        </div>
                        <div style={{ fontSize: "11.5px", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {modalUploadState.front.filename || frontImage.split("/").pop()}
                          {modalUploadState.front.size ? ` (${modalUploadState.front.size})` : ""}
                        </div>
                      </div>
                    </div>
                    <label style={{ margin: 0, cursor: "pointer", fontSize: "12px", color: "#0f172a", fontWeight: "700", background: "#ffffff", border: "1px solid #cbd5e1", padding: "5px 12px", borderRadius: "6px" }}>
                      🔄 Replace
                      <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => handleFileUpload(e, "front")} />
                    </label>
                  </div>
                ) : (
                  <label className="btn btn-outline-secondary btn-sm" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 16px", borderRadius: "8px", border: "1.5px dashed #cbd5e1", background: "#f8fafc", fontWeight: "700" }}>
                    <UploadCloudIcon size={16} />
                    <span>Select {selectedDocType === "aadhaar" ? "Front File" : "Document File"}</span>
                    <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => handleFileUpload(e, "front")} disabled={uploadingDoc} />
                  </label>
                )}
              </div>

              {/* Upload Back (for Aadhaar) */}
              {selectedDocType === "aadhaar" && (
                <div style={{ marginBottom: "18px" }}>
                  <label className="form-label" style={{ fontWeight: "700" }}>Back Side Photo / Scan (Optional)</label>
                  {modalUploadState.back.stage === "uploading" || modalUploadState.back.stage === "processing" ? (
                    <div className="doc-processing-card" style={{ border: "1.5px solid #3b82f6", borderRadius: "12px", padding: "16px 18px", position: "relative", overflow: "hidden" }}>
                      <div className="doc-scan-laser" />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <span style={{ fontSize: "12.5px", color: "#2563eb", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <span className="spinner-border spinner-border-sm" role="status" style={{ width: "12px", height: "12px", borderWidth: "2px" }} />
                          <span>{modalUploadState.back.stage === "processing" ? "Scanning Document Clarity..." : "Uploading securely..."}</span>
                        </span>
                        <span style={{ fontSize: "11.5px", fontWeight: "900", color: "#1d4ed8", background: "#dbeafe", padding: "2px 8px", borderRadius: "6px" }}>
                          {modalUploadState.back.percent}%
                        </span>
                      </div>
                      <div style={{ width: "100%", height: "7px", background: "#e2e8f0", borderRadius: "6px", overflow: "hidden", marginBottom: "12px" }}>
                        <div style={{ width: `${modalUploadState.back.percent}%`, height: "100%", background: "linear-gradient(90deg, #3b82f6, #10b981)", transition: "width 0.25s ease" }} />
                      </div>
                      {modalUploadState.back.filename && (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#ffffff", padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                          {modalUploadState.back.previewUrl ? (
                            <div className="doc-preview-wrapper" style={{ position: "relative", width: "48px", height: "34px", flexShrink: 0 }}>
                              <img src={modalUploadState.back.previewUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }} />
                              <div className="doc-scan-grid" />
                            </div>
                          ) : (
                            <span style={{ fontSize: "16px" }}>📄</span>
                          )}
                          <div style={{ minWidth: 0, flex: 1, fontSize: "12px", fontWeight: "700", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {modalUploadState.back.filename}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : backImage ? (
                    <div className="doc-added-card" style={{ border: "1.5px solid #86efac", borderRadius: "12px", padding: "14px 16px", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: 1 }}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <img
                            src={modalUploadState.back.previewUrl || getFullFileUrl(backImage)}
                            alt="Back"
                            style={{ width: "56px", height: "40px", objectFit: "cover", borderRadius: "6px", border: "1.5px solid #86efac" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                          <span style={{ position: "absolute", bottom: "-3px", right: "-3px", background: "#16a34a", color: "#ffffff", width: "16px", height: "16px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "900" }}>✓</span>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <strong style={{ fontSize: "13px", color: "#15803d" }}>Document Added ✓</strong>
                            <span style={{ fontSize: "10px", background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: "8px", fontWeight: "800" }}>Ready</span>
                          </div>
                          <div style={{ fontSize: "11.5px", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {modalUploadState.back.filename || backImage.split("/").pop()}
                            {modalUploadState.back.size ? ` (${modalUploadState.back.size})` : ""}
                          </div>
                        </div>
                      </div>
                      <label style={{ margin: 0, cursor: "pointer", fontSize: "12px", color: "#0f172a", fontWeight: "700", background: "#ffffff", border: "1px solid #cbd5e1", padding: "5px 12px", borderRadius: "6px" }}>
                        🔄 Replace
                        <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => handleFileUpload(e, "back")} />
                      </label>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 16px", borderRadius: "8px", border: "1.5px dashed #cbd5e1", background: "#f8fafc", fontWeight: "700" }}>
                      <UploadCloudIcon size={16} />
                      <span>Select Back File</span>
                      <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => handleFileUpload(e, "back")} disabled={uploadingDoc} />
                    </label>
                  )}
                </div>
              )}

              <p style={{ fontSize: "11.5px", color: "#64748b", marginBottom: "20px" }}>
                Accepted formats: JPEG, PNG, WEBP, PDF up to 5MB. Make sure text is clearly readable.
              </p>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button type="button" onClick={() => setShowDocModal(false)} className="btn btn-outline-secondary" style={{ fontSize: "13px" }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploadingDoc} style={{ fontSize: "13px", fontWeight: "700" }}>
                  {uploadingDoc ? "Uploading..." : "Submit for Verification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default VerificationCenter;
